import { useState } from 'react';
import { Bell, ChevronDown, LogOut, PanelLeftClose, User } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/app/store/auth.store';
import { useUiStore } from '@/app/store/ui.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { signOut } from 'firebase/auth';
import { auth } from '@/api/firebase';
import { logoutSession } from '@/api/auth';
import type { TopTab } from '@/shared/types/navigation';
import { useTranslation } from 'react-i18next';
import { ShcLogo } from '@/components/brand/ShcLogo';

interface GnbProps {
  variant: 'admin' | 'medical';
  topTabs?: TopTab[];
}

export function Gnb({ variant, topTabs = [] }: GnbProps) {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.email);
  const logout = useAuthStore((s) => s.logout);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const isUserMenuOpen = useUiStore((s) => s.isUserMenuOpen);
  const setUserMenuOpen = useUiStore((s) => s.setUserMenuOpen);
  const isNotificationMenuOpen = useUiStore((s) => s.isNotificationMenuOpen);
  const setNotificationMenuOpen = useUiStore((s) => s.setNotificationMenuOpen);

  // PRD 20 §3.5 — Logout confirmation popup, server session termination, and
  // failure popup on API error. The dropdown click opens the confirm modal;
  // only on Confirm does the actual server call + local clear happen.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutFailureOpen, setLogoutFailureOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const openLogoutConfirm = () => {
    setUserMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      // User-initiated logout: propagate a failed API call so the catch keeps the session and shows
      // the failure popup instead of clearing locally + redirecting (SHCT-331 / PRD 20 §3.5).
      await logoutSession({ throwOnError: true }); // clear the server-side refresh cookie so a later reload can't re-establish (FEATURE_005)
      await signOut(auth);
      setLogoutConfirmOpen(false);
      logout();
      navigate('/login', { replace: true });
    } catch {
      // PRD 20 §8.2 — Logout API failure → popup. Keep the session intact so
      // the user can retry; do not clear local auth state on failure.
      setLogoutConfirmOpen(false);
      setLogoutFailureOpen(true);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <header className={`gnb ${variant === 'medical' ? 'gnb--v3 gnb--dark' : ''}`}>
      <div className="gnb__left">
        <Link to="/home" className="gnb__logo">
          <div className="gnb__logo-icon"><ShcLogo size={18} /></div>
          {t('brand.serviceName', { ns: 'labels' })}
        </Link>

        {variant === 'admin' ? (
          <button type="button" className="gnb__toggle-btn" aria-label="사이드바 접기/펼치기" onClick={toggleSidebar}>
            <PanelLeftClose size={18} />
          </button>
        ) : (
          <nav className="gnb__tabs" aria-label="의료진 주요 메뉴">
            {topTabs.map((tab) => (
              <NavLink key={tab.path} to={tab.path} className={({ isActive }) => `gnb__tab ${isActive ? 'gnb__tab--active' : ''}`}>
                <tab.icon size={16} />{tab.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      <div className="gnb__right">
        {variant === 'medical' && (
          <div className="gnb__notif-wrap">
            <button type="button" className="gnb__notif-btn" title="알림" aria-label="알림" onClick={() => setNotificationMenuOpen(!isNotificationMenuOpen)}>
              <Bell size={18} />
              <span className="gnb__notif-badge">3</span>
            </button>
            <div className="gnb__notif-dropdown" data-visible={isNotificationMenuOpen ? 'true' : 'false'}>
              <div className="gnb__notif-dropdown-head"><span>최근 알림</span><Link to="/notifications" className="link-subtle">전체 보기 →</Link></div>
              <ul className="gnb__notif-list">
                <li className="gnb__notif-item gnb__notif-item--unread"><div className="gnb__notif-item-top"><span className="gnb__notif-item-title">주의 환자 상태 업데이트</span><span className="gnb__notif-item-time">방금</span></div></li>
                <li className="gnb__notif-item"><div className="gnb__notif-item-top"><span className="gnb__notif-item-title">오늘 일정 알림</span><span className="gnb__notif-item-time">10분 전</span></div></li>
              </ul>
            </div>
          </div>
        )}

        <button type="button" className="gnb__user-menu" data-open={isUserMenuOpen ? 'true' : 'false'} onClick={() => setUserMenuOpen(!isUserMenuOpen)}>
          <span>{email || (variant === 'medical' ? 'doctor@uuh.ulsan.kr' : 'root@uuh.ulsan.kr')}</span>
          {variant === 'admin' && <ChevronDown size={16} />}
        </button>
        <div className="gnb__dropdown" data-visible={isUserMenuOpen ? 'true' : 'false'}>
          <Link to="/my-profile" className="gnb__dropdown-item" onClick={() => setUserMenuOpen(false)}>{variant === 'admin' && <User size={16} />}{t('navigation.myProfile', { ns: 'common' })}</Link>
          {variant === 'medical' && <Link to="/settings" className="gnb__dropdown-item" onClick={() => setUserMenuOpen(false)}>{t('navigation.myProfile', { ns: 'common' })}</Link>}
          <div className="gnb__dropdown-divider" />
          <button type="button" className="gnb__dropdown-item" onClick={openLogoutConfirm}>{variant === 'admin' && <LogOut size={16} />}{t('actions.logout', { ns: 'common' })}</button>
        </div>
      </div>

      <ConfirmModal
        open={logoutConfirmOpen}
        title={t('logout.confirmTitle', { ns: 'common' })}
        message={t('logout.confirmMessage', { ns: 'common' })}
        confirmLabel={t('logout.confirmYes', { ns: 'common' })}
        cancelLabel={t('logout.confirmCancel', { ns: 'common' })}
        loading={loggingOut}
        loadingLabel={t('logout.loadingLabel', { ns: 'common' })}
        onConfirm={handleLogoutConfirm}
        onCancel={() => {
          if (loggingOut) return;
          setLogoutConfirmOpen(false);
        }}
      />

      <InfoModal
        open={logoutFailureOpen}
        title={t('logout.failureTitle', { ns: 'common' })}
        body={t('logout.failureMessage', { ns: 'common' })}
        confirmLabel={t('logout.failureClose', { ns: 'common' })}
        onClose={() => setLogoutFailureOpen(false)}
      />
    </header>
  );
}
